import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import logger from '../../utils/logger';
import { captureException } from '../../utils/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * Logs errors and displays a fallback UI
 */
class ErrorBoundary extends Component<Props, State> {
  private retryTimeout?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error:', error, errorInfo);

    // Check if it's a ChunkLoadError
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('ChunkLoadError') ||
      error?.message?.includes('Failed to fetch dynamically imported module');

    // Auto-retry for ChunkLoadErrors (max 2 retries)
    if (isChunkError && this.state.retryCount < 2) {
      logger.info(`ChunkLoadError detected. Auto-retry attempt ${this.state.retryCount + 1}/2`);
      this.setState((prevState) => ({ retryCount: prevState.retryCount + 1 }));

      // Retry after a short delay
      this.retryTimeout = setTimeout(() => {
        window.location.reload();
      }, 1000);
      return;
    }

    // Report to Sentry for production monitoring
    captureException(error, {
      componentStack: errorInfo.componentStack,
      retryCount: this.state.retryCount,
      isChunkError,
    });

    this.setState({ error, errorInfo });
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, retryCount: 0 });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Check if it's a ChunkLoadError
      const isChunkError =
        this.state.error?.name === 'ChunkLoadError' ||
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.message?.includes('ChunkLoadError') ||
        this.state.error?.message?.includes('Failed to fetch dynamically imported module');

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-zink-900 p-4">
          <div className="max-w-2xl w-full">
            <div className="card shadow-lg">
              <div className="card-body p-8">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                    <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-500" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-center text-slate-800 dark:text-zink-50 mb-3">
                  {isChunkError ? 'Application Update Required' : 'Oops! Something went wrong'}
                </h1>
                <p className="text-center text-slate-600 dark:text-zink-300 mb-4">
                  {isChunkError ? (
                    'A new version of the application is available. Please refresh the page to load the latest version.'
                  ) : (
                    'We\'re sorry for the inconvenience. An unexpected error occurred.'
                  )}
                </p>
                {isChunkError && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                    <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
                      <strong>Tip:</strong> If the issue persists after refreshing, try:<br />
                      • Hard refresh: <code className="bg-amber-100 dark:bg-amber-900 px-2 py-1 rounded">Ctrl+Shift+R</code> (Windows) or <code className="bg-amber-100 dark:bg-amber-900 px-2 py-1 rounded">Cmd+Shift+R</code> (Mac)<br />
                      • Clear your browser cache<br />
                      {this.state.retryCount > 0 && (
                        <span className="text-xs">• Failed after {this.state.retryCount} automatic retry attempt(s)</span>
                      )}
                    </p>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => window.location.reload()}
                    className="btn bg-custom-500 hover:bg-custom-600 text-white px-6 py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" /> Refresh Page
                  </button>
                  <button onClick={this.handleGoHome} className="btn border border-slate-300 dark:border-zink-500 bg-white dark:bg-zink-700 text-slate-700 dark:text-zink-200 px-6 py-2.5 rounded-md inline-flex items-center justify-center gap-2">
                    <Home className="w-4 h-4" /> Go to Homepage
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
